import React, { useState, useEffect, useRef } from "reactn"

import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js"

import {
  Center,
  Alert,
  AlertIcon,
  AlertDescription,
  Box,
} from "@chakra-ui/react"

import { Dots } from "./dotsAnimation"

import { paymentFailed } from "../../state/analytics"

import "./cartSidebar.css"

const PaymentForm = ({ globalState, setProcessing, setIsReady }) => {
  const { store, updateGlobal } = globalState

  const {
    fetchingCartData,
    cartTotal,
    fullName,
    contactNumber,
    emailAddress,
    processPayment,
    shipping_address,
    confirmationTokenId,
    pd,
    calculating,
    processing,
    cartProducts, // Track cart items to regenerate token when cart changes
  } = store

  const [succeeded, setSucceeded] = useState(false)

  const [error, setError] = useState(null)

  const [disabled, setDisabled] = useState(true)

  // Track if PaymentElement is ready
  const [elementReady, setElementReady] = useState(false)
  const lastElementsRef = useRef(null)

  const types = {
    accordion: {
      type: "accordion",

      defaultCollapsed: false,

      radios: true,

      spacedAccordionItems: true,
    },

    tabs: {
      type: "tabs",
    },
  }

  const stripe = useStripe()

  const elements = useElements()

  // DO NOT overwrite elementsForConfirmation from PaymentForm!
  // PaymentForm only has PaymentElement, not AddressElement
  // CheckoutContent has AddressElement and should be the source of truth
  // Only update if we don't already have elements with AddressElement
  useEffect(() => {
    if (stripe && elements && elements !== lastElementsRef.current) {
      lastElementsRef.current = elements

      // Check if current elements has AddressElement
      const hasAddressElement = !!elements.getElement('address')
      const currentElements = store.elementsForConfirmation
      const currentHasAddress = currentElements ? !!currentElements.getElement('address') : false

      console.log('[PAYMENT FORM] Elements check:', {
        hasAddressElement: hasAddressElement,
        currentHasAddress: currentHasAddress,
        shouldUpdate: !currentHasAddress || hasAddressElement
      })

      // Only update if current elements doesn't have AddressElement, or if this one does
      // This prevents PaymentForm from overwriting CheckoutContent's elements
      if (!currentHasAddress || hasAddressElement) {
        updateGlobal({
          stripeForConfirmation: stripe,
          elementsForConfirmation: elements
        })
      } else {
        console.warn('[PAYMENT FORM] Not updating elementsForConfirmation - current elements has AddressElement, this one does not')
      }
    }
  }, [stripe, elements]) // eslint-disable-line react-hooks/exhaustive-deps

  function setProcessPayment(processPayment) {
    updateGlobal({ processPayment: processPayment })
  }

  const handleChange = async (event) => {
    setDisabled(event.complete ? false : true)

    setIsReady(event.complete ? true : false)

    // Mark element as ready when it first becomes complete
    if (event.complete && !elementReady) {
      setElementReady(true)
    }

    if (event.error) {
      setProcessing(false)

      setError(event.error.message)
    } else {
      setError("")
    }
  }

  // SECURITY: ALWAYS regenerate ConfirmationToken whenever PaymentElement is ready
  // This ensures the token always matches current state (address, email, cart items)
  // Cart items affect shipping, so token must be regenerated when cart changes
  // ConfirmationToken is now only created when "PAY NOW" is clicked in checkout.js
  // No automatic token creation here - removed to follow Stripe two-step confirmation flow

  useEffect(() => {
    if (processPayment == true) {
      console.log(
        "[PAYMENT FORM] processPayment triggered - calling handleCheckout"
      )

      setProcessPayment(false)

      handleCheckout()
    }
  }, [processPayment])

  const handleCheckout = async () => {
    console.log("[PAYMENT FORM] handleCheckout called")

    console.log("[PAYMENT FORM] Stripe instance:", !!stripe)

    console.log("[PAYMENT FORM] Elements instance:", !!elements)

    console.log("[PAYMENT FORM] Email address:", emailAddress)

    console.log("[PAYMENT FORM] Return URL:", process.env.GATSBY_RETURN_URL)

    if (!stripe || !elements) {
      console.error("[PAYMENT FORM] Missing stripe or elements:", {
        stripe: !!stripe,
        elements: !!elements,
      })

      return
    }

    // SECURITY: ConfirmationToken is MANDATORY - cannot confirm payment without it
    // It should already exist (created when "PAY NOW" is clicked in checkout.js)
    if (!confirmationTokenId) {
      console.error("[PAYMENT FORM] SECURITY ERROR: No ConfirmationToken - cannot confirm payment")
      setError("Address validation required. Please complete your address details.")
      setProcessing(false)
      return
    }

    const returnURL = process.env.GATSBY_RETURN_URL
    alert(returnURL)
    console.log("[PAYMENT FORM] Return URL:", returnURL)
    // SECURITY: ALWAYS use ConfirmationToken - it's mandatory
    const confirmParams = {
      return_url: returnURL,
      // receipt_email: emailAddress,
      confirmation_token: confirmationTokenId, // MANDATORY - always include
    }

    console.log("[PAYMENT FORM] SECURITY: Confirming payment with ConfirmationToken:", confirmationTokenId)

    try {
      // SECURITY: When using ConfirmationToken, do NOT pass elements
      // Stripe requires either elements OR confirmation_token, not both
      const { error, paymentIntent } = await stripe.confirmPayment({
        clientSecret: pd?.cs, // Required: client secret
        confirmParams, // Contains confirmation_token
      })

      console.log("[PAYMENT FORM] confirmPayment call completed")

      console.log("[PAYMENT FORM] Error:", error)

      console.log("[PAYMENT FORM] Payment Intent:", paymentIntent)

      if (error) {
        console.error("[PAYMENT FORM] Payment error occurred:", {
          type: error.type,

          message: error.message,

          code: error.code,
        })

        if (error.type === "card_error" || error.type === "validation_error") {
          setError(error.message)
        } else {
          setError("An unexpected error occured.")
        }

        paymentFailed(store, {
          type: error.type,
          message: error.message,
          code: error.code,
        })

        updateGlobal({ calculating: false, processPayment: false })

        setProcessing(false)

        setSucceeded(false)
      } else {
        console.log("[PAYMENT FORM] Payment succeeded!")

        console.log(
          "[PAYMENT FORM] Payment Intent status:",
          paymentIntent?.status
        )

        console.log("[PAYMENT FORM] Payment Intent ID:", paymentIntent?.id)

        // Check for redirect (3D Secure)

        if (paymentIntent?.next_action) {
          console.log(
            "[PAYMENT FORM] Next action required:",
            paymentIntent.next_action.type
          )

          if (paymentIntent.next_action.type === "redirect_to_url") {
            console.log(
              "[PAYMENT FORM] Redirect URL:",
              paymentIntent.next_action.redirect_to_url.url
            )

            console.log("[PAYMENT FORM] Browser will redirect automatically")
          }
        }

        setError(null)

        setSucceeded(true)
      }
    } catch (exception) {
      console.error(
        "[PAYMENT FORM] Exception during confirmPayment:",
        exception
      )

      console.error("[PAYMENT FORM] Exception stack:", exception.stack)

      setError("An unexpected error occured.")

      paymentFailed(store, { type: "exception", message: exception.message })

      updateGlobal({ calculating: false, processPayment: false })

      setProcessing(false)

      setSucceeded(false)
    }
  }

  return (
    <>
      {!succeeded && (
        <>
          {fetchingCartData ? (
            <Box>
              <Center>
                <Dots mb={5} size="xs" />
              </Center>
            </Box>
          ) : (
            <>
              <form onSubmit={handleCheckout} id="payment">
                {error && (
                  <Alert status="error">
                    <AlertIcon />

                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Center>
                  <Box mt={2} maxWidth={"600px"} width="100%">
                    <PaymentElement
                      id="payment-element"
                      onChange={handleChange}
                      options={{
                        defaultValues: {
                          billingDetails: {
                            name: fullName,

                            phone: contactNumber,

                            email: emailAddress,
                          },
                        },

                        layout: types["accordion"],

                        fields: {
                          billingDetails: {
                            address: {
                              email: "always",

                              name: "always",

                              phone: "always",

                              address: "always",
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </Center>
              </form>
            </>
          )}
        </>
      )}
    </>
  )
}

export default PaymentForm
